import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "react-hot-toast";
import Layout from "./components/layout/Layout";
import MainSection from "./components/layout/MainSection";
import AboutTeam from "./components/layout/AboutTeam";
import ProgramsSection from "./components/layout/ProgramsSection";
import HeadCoach from "./components/layout/HeadCoach";
import TeamMembersSection from "./components/layout/TeamMembersSection";
import { EventsSection } from "./components/events";
import PartnersSection from "./components/layout/PartnersSection";
import ProgramPage from "./components/layout/ProgramPage";
import ProgramCatalogPage from "./components/layout/ProgramCatalogPage";
import TrainersPage from "./components/layout/TrainersPage";
import AnimatedLoginForm from "./components/auth/AnimatedLoginForm";
import MetaPixel from './components/MetaPixel';
import Analytics from './components/Analytics';

// Pages
import UserDashboard from "./components/dashboard/UserDashboard";
import ShopPage from "./components/shop/ShopPage";
import LegalPage from "./pages/LegalPage";

// Hooks
import { useAuth } from "./contexts/AuthContext";
import { trackPageView } from './utils/analytics';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/auth" replace />;
};

// Main App Component
const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastNonAuthLocationRef = useRef(location);
  const isAuthModalOpen = location.pathname === '/auth';

  useEffect(() => {
    if (location.pathname !== '/auth') {
      lastNonAuthLocationRef.current = location;
    }
  }, [location]);

  useEffect(() => {
    if (!isAuthModalOpen) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') {
        if (lastNonAuthLocationRef.current.pathname === '/auth') {
          navigate('/', { replace: true });
        } else {
          navigate(-1);
        }
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [isAuthModalOpen, navigate]);

  useEffect(() => {
    if (!isAuthModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAuthModalOpen]);

  const hasBackgroundRoute = isAuthModalOpen && lastNonAuthLocationRef.current.pathname !== '/auth';
  const routingLocation = hasBackgroundRoute
    ? lastNonAuthLocationRef.current
    : (isAuthModalOpen ? { ...location, pathname: '/' } : location);

  const closeAuthModal = () => {
    if (lastNonAuthLocationRef.current.pathname === '/auth') {
      navigate('/', { replace: true });
      return;
    }
    navigate(-1);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <ScrollToTop />
      <MetaPixel />
      <Analytics />
      <Routes location={routingLocation}>
        {/* Все остальные страницы - с Header и Footer */}
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/events" element={<Layout><EventsPage /></Layout>} />
        <Route path="/team" element={<Layout><TeamPage /></Layout>} />
        <Route path="/reviews" element={<Navigate to="/" replace />} />
        <Route path="/coaches" element={<Layout><TrainersPage /></Layout>} />
        <Route path="/trainers" element={<Navigate to="/coaches" replace />} />
        <Route path="/power-training" element={<Navigate to="/program/power-training" replace />} />
        <Route path="/programs" element={<Layout><ProgramCatalogPage /></Layout>} />
        <Route path="/program/:slug" element={<Layout><ProgramPage /></Layout>} />
        <Route path="/lp/:sport/:slug" element={<Layout><ProgramPage /></Layout>} />
        <Route path="/privacy" element={<Layout><LegalPage page="privacy" /></Layout>} />
        <Route path="/support" element={<Layout><LegalPage page="support" /></Layout>} />
        
        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <Layout hideFooter mainClassName="bg-slate-100">
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            </Layout>
          } 
        />
        <Route 
          path="/shop" 
          element={
            <Layout hideFooter mainClassName="bg-slate-100">
              <ProtectedRoute>
                <ShopPage />
              </ProtectedRoute>
            </Layout>
          } 
        />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {isAuthModalOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/62 backdrop-blur-md flex items-center justify-center p-4"
          onClick={closeAuthModal}
        >
          <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeAuthModal}
              className="absolute -top-3 -right-3 z-10 p-2 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
              aria-label="Закрыть окно входа"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <AnimatedLoginForm embedded />
          </div>
        </div>
      )}
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === '/auth') return;
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Page Components
const HomePage = () => {
        trackPageView('Home');
        return (
          <main>
            <MainSection />
            <AboutTeam />
            <HeadCoach />
            <ProgramsSection />
            {/* <ReviewsCarousel /> */}
            <PartnersSection />
          </main>
        );
};

const EventsPage = () => {
  trackPageView('Events');
  return <EventsSection />;
};

const TeamPage = () => {
  trackPageView('Team');
  return <TeamMembersSection />;
  };

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" />
      </AuthProvider>
    </Router>
  );
}

export default App;
